import React, { useEffect, useRef, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import mailboxAxios from "../../utils/mailboxAxios";

export default function MailboxHome() {
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loadingThreads, setLoadingThreads] = useState(false);
  const [loadingThread, setLoadingThread] = useState(false);
  const [error, setError] = useState("");
  const [authReady, setAuthReady] = useState(false);

  const [mobileView, setMobileView] = useState("list"); // "list" | "thread"

  const [folders, setFolders] = useState([]);

const [activeFolder, setActiveFolder] = useState(null);

  const threadScrollRef = useRef(null);
  const savedThreadScrollTop = useRef(0);
  const autoRefreshRef = useRef(false);

  const [foldersOpen, setFoldersOpen] = useState(false);

  const [composeOpen, setComposeOpen] = useState(false);
const [sending, setSending] = useState(false);
const [savingDraft, setSavingDraft] = useState(false);

const [signatureProfile, setSignatureProfile] = useState({
  full_name: "",
  first_name: "",
  last_name: "",
  display_name: "",
  email: "",
  job_title: "",
  department: "",
  phone: "",
  mobile: "",
  website: "",
  company_name: "",
  company_address: "",
  city: "",
  country: "",
  linkedin_url: "",
  instagram_url: "",
  facebook_url: "",
  pinterest_url: "",
  twitter_url: "",
  whatsapp_url: "",
  telegram_url: "",
  youtube_url: "",
  vcard_url: "",
  tagline: "",
  logo_url: "",
  profile_image_url: "",
  accent_color: "#d6c176",
});

const [signatureProfileOpen, setSignatureProfileOpen] = useState(false);
const [savingSignatureProfile, setSavingSignatureProfile] = useState(false);



const [recipientQuery, setRecipientQuery] = useState({
  to: "",
  cc: "",
  bcc: "",
});

const [recipientSuggestions, setRecipientSuggestions] = useState({
  to: [],
  cc: [],
  bcc: [],
});

const [recipientLoading, setRecipientLoading] = useState({
  to: false,
  cc: false,
  bcc: false,
});

const [composeForm, setComposeForm] = useState({
  draftId: null,
  to: [],
  cc: [],
  bcc: [],
  subject: "",
  text: "",
  html: "",
});

const [activeRecipientBox, setActiveRecipientBox] = useState(null);

const [signatures, setSignatures] = useState([]);
const [selectedSignatureId, setSelectedSignatureId] = useState("");
const [signatureEditorOpen, setSignatureEditorOpen] = useState(false);

const [signatureForm, setSignatureForm] = useState({
  id: null,
  name: "",
  html_signature: "",
  is_default: false,
  is_auto_generated: false,
  source_type: "manual",
});

const isLockedSignature = !!signatureForm.is_auto_generated;

const [signaturePreviewOpen, setSignaturePreviewOpen] = useState(false);

function resetComposeForm() {
  setComposeForm({
    draftId: null,
    to: [],
    cc: [],
    bcc: [],
    subject: "",
    text: "",
    html: "",
  });

  setRecipientQuery({
    to: "",
    cc: "",
    bcc: "",
  });

  setRecipientSuggestions({
    to: [],
    cc: [],
    bcc: [],
  });

  setActiveRecipientBox(null);
}

async function searchRecipients(field, query) {
  const q = String(query || "").toLowerCase().trim();

  if (!q) {
    setRecipientSuggestions((prev) => ({ ...prev, [field]: [] }));
    return;
  }

  try {
    setRecipientLoading((prev) => ({ ...prev, [field]: true }));

    // 🔥 fallback iz threadova
    const emailsMap = new Map();

    threads.forEach((t) => {
      if (t.last_from_email) {
        emailsMap.set(t.last_from_email, {
          email: t.last_from_email,
          label: t.last_from_name || t.last_from_email,
        });
      }
    });

    const results = Array.from(emailsMap.values()).filter((x) =>
      x.email.toLowerCase().includes(q) ||
      (x.label || "").toLowerCase().includes(q)
    );

    setRecipientSuggestions((prev) => ({
      ...prev,
      [field]: results.slice(0, 10),
    }));
  } catch (err) {
    console.error("SEARCH RECIPIENTS ERROR:", err);
  } finally {
    setRecipientLoading((prev) => ({ ...prev, [field]: false }));
  }
}

useEffect(() => {
  const t = setTimeout(() => {
    if (activeRecipientBox === "to") {
      searchRecipients("to", recipientQuery.to);
    }
  }, 250);

  return () => clearTimeout(t);
}, [recipientQuery.to, activeRecipientBox]);

useEffect(() => {
  const t = setTimeout(() => {
    if (activeRecipientBox === "cc") {
      searchRecipients("cc", recipientQuery.cc);
    }
  }, 250);

  return () => clearTimeout(t);
}, [recipientQuery.cc, activeRecipientBox]);

useEffect(() => {
  const t = setTimeout(() => {
    if (activeRecipientBox === "bcc") {
      searchRecipients("bcc", recipientQuery.bcc);
    }
  }, 250);

  return () => clearTimeout(t);
}, [recipientQuery.bcc, activeRecipientBox]);

function addRecipient(field, item) {
  const email = String(item?.email || "").trim();
  const label = String(item?.label || item?.name || email).trim();

  if (!email) return;

  setComposeForm((prev) => {
    const exists = prev[field].some(
      (x) => String(x.email).toLowerCase() === email.toLowerCase()
    );

    if (exists) return prev;

    return {
      ...prev,
      [field]: [...prev[field], { label, email }],
    };
  });

  setRecipientQuery((prev) => ({ ...prev, [field]: "" }));
  setRecipientSuggestions((prev) => ({ ...prev, [field]: [] }));
}

function removeRecipient(field, email) {
  setComposeForm((prev) => ({
    ...prev,
    [field]: prev[field].filter(
      (x) => String(x.email).toLowerCase() !== String(email).toLowerCase()
    ),
  }));
}

function addManualRecipient(field) {
  const value = String(recipientQuery[field] || "").trim();
  if (!value) return;

  const email = value.replace(/[;,]+$/, "").trim();

  const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  if (!isValid) return;

  addRecipient(field, { label: email, email });
}

function renderRecipientField(field, label, placeholder) {
  const recipients = Array.isArray(composeForm[field]) ? composeForm[field] : [];
  const suggestions = Array.isArray(recipientSuggestions[field]) ? recipientSuggestions[field] : [];

  return (
    <div className="relative">
      <div className="text-sm mb-1">{label}</div>

      <div className="w-full px-2 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 min-h-[44px] flex flex-wrap items-center gap-2">
        {recipients.map((item) => (
          <span
            key={`${field}-${item.email}`}
            className="inline-flex items-center gap-2 px-2 py-1 rounded-full bg-neutral-100 dark:bg-neutral-800 text-xs sm:text-sm"
          >
            <span className="max-w-[180px] truncate">
              {item.label || item.email}
            </span>
            <button
              type="button"
              onClick={() => removeRecipient(field, item.email)}
              className="text-neutral-500 hover:text-neutral-900 dark:hover:text-white"
            >
              ×
            </button>
          </span>
        ))}

        <input
          value={recipientQuery[field] || ""}
          onFocus={() => setActiveRecipientBox(field)}
          onChange={(e) =>
            setRecipientQuery((prev) => ({
              ...prev,
              [field]: e.target.value,
            }))
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === "," || e.key === ";") {
              e.preventDefault();
              addManualRecipient(field);
            }

            if (e.key === "Backspace" && !(recipientQuery[field] || "") && recipients.length) {
              const last = recipients[recipients.length - 1];
              removeRecipient(field, last.email);
            }
          }}
          placeholder={placeholder}
          className="flex-1 min-w-[180px] bg-transparent outline-none text-sm"
        />
      </div>

      {activeRecipientBox === field &&
        (recipientLoading[field] || suggestions.length > 0 || recipientQuery[field]) && (
          <div className="absolute z-20 mt-1 w-full rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-950 shadow-lg overflow-hidden">
            {recipientLoading[field] && (
              <div className="px-3 py-2 text-sm text-neutral-500">Searching...</div>
            )}

            {!recipientLoading[field] &&
              suggestions.map((item) => (
                <button
                  key={`${field}-${item.type || "contact"}-${item.id}-${item.email}`}
                  type="button"
                  onClick={() => addRecipient(field, item)}
                  className="w-full text-left px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                >
                  <div className="text-sm font-medium break-words">
                    {item.label || item.email}
                  </div>
                  <div className="text-xs text-neutral-500 break-all">
                    {item.email}
                    {item.meta ? ` • ${item.meta}` : ""}
                  </div>
                </button>
              ))}

            {!recipientLoading[field] &&
              !suggestions.length &&
              recipientQuery[field] && (
                <button
                  type="button"
                  onClick={() => addManualRecipient(field)}
                  className="w-full text-left px-3 py-2 hover:bg-neutral-100 dark:hover:bg-neutral-900"
                >
                  <div className="text-sm font-medium">Use typed email</div>
                  <div className="text-xs text-neutral-500 break-all">
                    {recipientQuery[field]}
                  </div>
                </button>
              )}
          </div>
        )}
    </div>
  );
}

async function loadSignatures() {
  try {
    const { data } = await mailboxAxios.get("/mailbox/signatures");
    const items = Array.isArray(data.items) ? data.items : [];

    setSignatures(items);

    const defaultSignature =
      items.find((s) => Number(s.is_default) === 1) || items[0] || null;

    setSelectedSignatureId(defaultSignature ? String(defaultSignature.id) : "");

    return items;
  } catch (err) {
    console.error("LOAD SIGNATURES ERROR:", err);
    setError(err?.response?.data?.error || "Failed to load signatures");
    return [];
  }
}

function buildHtmlWithSignature(messageText, signatureHtml) {
  const escapedMessage = String(messageText || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\n/g, "<br />");

  if (!signatureHtml) {
    return `<div>${escapedMessage}</div>`;
  }

  return `
    <div>${escapedMessage}</div>
    <div style="margin-top:16px;">
      ${signatureHtml}
    </div>
  `;
}



async function handleSaveDraft() {
  const toList = Array.isArray(composeForm.to) ? composeForm.to : [];
const ccList = Array.isArray(composeForm.cc) ? composeForm.cc : [];
const bccList = Array.isArray(composeForm.bcc) ? composeForm.bcc : [];
  if (!accountId) return;

  try {
    setSavingDraft(true);
    setError("");

    const selectedSignature =
      signatures.find((s) => String(s.id) === String(selectedSignatureId)) || null;

    const payload = {
  accountId,
  to: toList.map((x) => x.email),
  cc: ccList.map((x) => x.email),
  bcc: bccList.map((x) => x.email),
  subject: composeForm.subject,
  text: composeForm.text,
  html: buildHtmlWithSignature(
    composeForm.text,
    selectedSignature?.html_signature || ""
  ),
};

    let data;

    if (composeForm.draftId) {
      const res = await mailboxAxios.patch(
        `/mailbox/drafts/${composeForm.draftId}`,
        payload
      );
      data = res.data;
    } else {
      const res = await mailboxAxios.post(`/mailbox/drafts`, payload);
      data = res.data;

      if (data?.mailId) {
        setComposeForm((prev) => ({
          ...prev,
          draftId: data.mailId,
        }));
      }
    }

    await loadFolders(accountId);
    await loadThreads({ preserveSelection: true });
  } catch (err) {
    console.error("SAVE DRAFT ERROR:", err);
    setError(err?.response?.data?.error || "Failed to save draft");
  } finally {
    setSavingDraft(false);
  }
}

async function handleSendEmail() {
  const toList = Array.isArray(composeForm.to) ? composeForm.to : [];
const ccList = Array.isArray(composeForm.cc) ? composeForm.cc : [];
const bccList = Array.isArray(composeForm.bcc) ? composeForm.bcc : [];
  if (!accountId) return;
  if (!toList.length) {
  setError("Recipient is required");
  return;
}

  try {
    setSending(true);
    setError("");

    const selectedSignature =
      signatures.find((s) => String(s.id) === String(selectedSignatureId)) || null;

    const payload = {
  accountId,
  to: toList.map((x) => x.email),
  cc: ccList.map((x) => x.email),
  bcc: bccList.map((x) => x.email),
  subject: composeForm.subject,
  text: composeForm.text,
  html: buildHtmlWithSignature(
    composeForm.text,
    selectedSignature?.html_signature || ""
  ),
  draftId: composeForm.draftId || null,
};

    await mailboxAxios.post(`/mailbox/send`, payload);

    setComposeOpen(false);
    resetComposeForm();

    await loadFolders(accountId);
    await loadThreads({ preserveSelection: false });
  } catch (err) {
    console.error("SEND EMAIL ERROR:", err);
    setError(err?.response?.data?.error || "Failed to send email");
  } finally {
    setSending(false);
  }
}

  async function loadAccounts() {
    try {
      setError("");

      const { data } = await mailboxAxios.get("/mailbox/accounts");
      const items = data.items || [];

      setAccounts(items);

      if (items.length) {
        setAccountId((prev) => prev ?? items[0].id);
      } else {
        setAccountId(null);
        setThreads([]);
        setSelectedThread(null);
        setSelectedThreadId(null);
      }
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to load accounts");
    }
  }

  useEffect(() => {
    const auth = getAuth();

    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthReady(true);
        loadAccounts();
      } else {
        setAuthReady(true);
        setError("User is not authenticated");
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
  if (!authReady || !accountId || !activeFolder) return;

  setSelectedThreadId(null);
  setSelectedThread(null);
  setMobileView("list");

  loadThreads({ preserveSelection: false });
}, [authReady, accountId, activeFolder?.uniqueKey]);

  useEffect(() => {
  if (!authReady || !accountId) return;

  setSelectedThreadId(null);
  setSelectedThread(null);
  setThreads([]);
  setMobileView("list");
  setActiveFolder(null);

  loadFolders(accountId);
}, [authReady, accountId]);

  async function loadFolders(selectedAccountId) {
  try {
    setError("");

    const { data } = await mailboxAxios.get(
      `/mailbox/folders?accountId=${selectedAccountId}`
    );

    const rawDbFolders = data.items || [];

const dbFolders = rawDbFolders
  .filter((f) => {
    const folderName = String(f.folder_name || "").toLowerCase();
    const folderPath = String(f.folder_path || "").toLowerCase();

    if (folderName.startsWith("__virtual__/")) return false;
    if (folderPath.startsWith("__virtual__/")) return false;

    return true;
  })
  .map((f) => {
    const value = `${f.folder_name || ""} ${f.folder_path || ""}`.toLowerCase();

    let system_key = "";
    if (value.includes("inbox")) system_key = "inbox";
    else if (value.includes("sent")) system_key = "sent";
    else if (value.includes("draft")) system_key = "drafts";
    else if (value.includes("trash") || value.includes("deleted")) system_key = "trash";
    else if (value.includes("spam") || value.includes("junk")) system_key = "spam";
    else if (value.includes("archive")) system_key = "archive";

    return {
      ...f,
      name: f.folder_name,
      system_key,
      isVirtual: false,
      uniqueKey: `db-${f.id}`,
    };
  });

const virtualCounts = data.virtualCounts || {};

const hasInbox = dbFolders.some((f) => f.system_key === "inbox");
const hasStarred = dbFolders.some((f) => f.system_key === "starred");
const hasTrash = dbFolders.some((f) => f.system_key === "trash");
const hasSent = dbFolders.some((f) => f.system_key === "sent");
const hasDrafts = dbFolders.some((f) => f.system_key === "drafts");

const inboxPlaceholder = !hasInbox
  ? {
      id: null,
      folder_name: "Inbox",
      folder_path: "__virtual__/inbox",
      name: "Inbox",
      system_key: "inbox",
      total_messages: 0,
      unread_messages: 0,
      isVirtual: true,
      uniqueKey: "virtual-inbox",
    }
  : null;

const virtualFolders = [
  !hasStarred
    ? {
        id: null,
        folder_name: "Starred",
        folder_path: "__virtual__/starred",
        name: "Starred",
        system_key: "starred",
        total_messages: Number(virtualCounts.starred || 0),
        unread_messages: 0,
        isVirtual: true,
        uniqueKey: "virtual-starred",
      }
    : null,
  !hasTrash
    ? {
        id: null,
        folder_name: "Trash",
        folder_path: "__virtual__/trash",
        name: "Trash",
        system_key: "trash",
        total_messages: Number(virtualCounts.trash || 0),
        unread_messages: 0,
        isVirtual: true,
        uniqueKey: "virtual-trash",
      }
    : null,
].filter(Boolean);

const placeholderFolders = [
  !hasSent
    ? {
        id: null,
        folder_name: "Sent",
        folder_path: "__virtual__/sent",
        name: "Sent",
        system_key: "sent",
        total_messages: Number(virtualCounts.sent || 0),
        unread_messages: 0,
        isVirtual: true,
        uniqueKey: "virtual-sent",
      }
    : null,
  !hasDrafts
    ? {
        id: null,
        folder_name: "Drafts",
        folder_path: "__virtual__/drafts",
        name: "Drafts",
        system_key: "drafts",
        total_messages: Number(virtualCounts.drafts || 0),
        unread_messages: 0,
        isVirtual: true,
        uniqueKey: "virtual-drafts",
      }
    : null,
].filter(Boolean);

const items = [
  ...(inboxPlaceholder ? [inboxPlaceholder] : []),
  ...dbFolders,
  ...virtualFolders,
  ...placeholderFolders,
];

setFolders(items);

const inbox =
  items.find((f) => f.system_key === "inbox") ||
  items[0] ||
  null;

setActiveFolder(inbox);
  } catch (err) {
    setError(err?.response?.data?.error || "Failed to load folders");
  }
}

useEffect(() => {
  if (!authReady) return;
  loadSignatures();
}, [authReady]);

useEffect(() => {
  if (!authReady) return;
  loadSignatureProfile();
}, [authReady]);


  useEffect(() => {
    if (selectedThreadId) {
      loadThread(selectedThreadId, { preserveScroll: false });
    }
  }, [selectedThreadId]);

  useEffect(() => {
  if (!authReady || !accountId || !activeFolder) return;
  if (activeFolder.isVirtual) return;

  const interval = setInterval(() => {
    refreshMailbox();
  }, 30000);

  return () => clearInterval(interval);
}, [authReady, accountId, selectedThreadId, activeFolder?.uniqueKey]);

  async function loadThreads({ preserveSelection = true } = {}) {
  if (!accountId || !activeFolder) return;

  try {
    setLoadingThreads(true);
    setError("");

    const params = new URLSearchParams({
      accountId: String(accountId),
      limit: "50",
    });

    if (activeFolder.isVirtual) {
      params.set("folderKey", activeFolder.system_key);
    } else if (activeFolder.id) {
      params.set("folderId", String(activeFolder.id));
    }

    const url = `/mailbox/threads?${params.toString()}`;

    const { data } = await mailboxAxios.get(url);

    const nextThreads = data.items || [];
    setThreads(nextThreads);

    if (!nextThreads.length) {
      setSelectedThreadId(null);
      setSelectedThread(null);
      return;
    }

    if (!preserveSelection) {
      setSelectedThreadId(nextThreads[0].id);
      return;
    }

    const stillExists = selectedThreadId
      ? nextThreads.some((t) => t.id === selectedThreadId)
      : false;

    if (!selectedThreadId || !stillExists) {
      setSelectedThreadId(nextThreads[0].id);
    }
  } catch (err) {
    setError(err?.response?.data?.error || err.message || "Failed to load threads");
  } finally {
    setLoadingThreads(false);
  }
}

 async function loadThread(threadId, { preserveScroll = true } = {}) {
  if (!threadId) return;

  try {
    if (preserveScroll && threadScrollRef.current) {
      savedThreadScrollTop.current = threadScrollRef.current.scrollTop;
    }

    if (!autoRefreshRef.current) {
      setLoadingThread(true);
    }

    setError("");

    const params = new URLSearchParams();

    if (activeFolder?.isVirtual) {
      params.set("folderKey", activeFolder.system_key);
    } else if (activeFolder?.id) {
      params.set("folderId", String(activeFolder.id));
    }

    const { data } = await mailboxAxios.get(
      `/mailbox/thread/${threadId}?${params.toString()}`
    );

    setSelectedThread(data);

    requestAnimationFrame(() => {
      if (preserveScroll && threadScrollRef.current) {
        threadScrollRef.current.scrollTop = savedThreadScrollTop.current;
      }
    });
  } catch (err) {
    setError(err?.response?.data?.error || "Failed to load thread");
  } finally {
    setLoadingThread(false);
    autoRefreshRef.current = false;
  }
}

  async function refreshMailbox() {
  if (!accountId || !activeFolder) return;

  try {
    setError("");
    autoRefreshRef.current = true;

    if (threadScrollRef.current) {
      savedThreadScrollTop.current = threadScrollRef.current.scrollTop;
    }

    // Virtualni folderi nemaju pravi IMAP path i ne smeju na sync
    if (!activeFolder.isVirtual) {
      await mailboxAxios.post("/mailbox/sync", {
        accountId,
        folder: activeFolder.folder_path || "Inbox",
      });
    }

    await loadThreads({ preserveSelection: true });

    if (selectedThreadId) {
      await loadThread(selectedThreadId, { preserveScroll: true });
    }
  } catch (err) {
    setError(err?.response?.data?.error || err.message || "Failed to refresh mailbox");
    autoRefreshRef.current = false;
  }
}

 async function loadSignatureProfile() {
  try {
    const { data } = await mailboxAxios.get("/mailbox/signature-profile/me");
    setSignatureProfile(
      data?.item || {
        full_name: "",
        first_name: "",
        last_name: "",
        display_name: "",
        email: "",
        job_title: "",
        department: "",
        phone: "",
        mobile: "",
        website: "",
        company_name: "",
        company_address: "",
        city: "",
        country: "",
        linkedin_url: "",
        instagram_url: "",
        facebook_url: "",
        pinterest_url: "",
        twitter_url: "",
        whatsapp_url: "",
        telegram_url: "",
        youtube_url: "",
        vcard_url: "",
        tagline: "",
        logo_url: "",
        profile_image_url: "",
        accent_color: "#d6c176",
      }
    );
  } catch (err) {
    console.error("LOAD SIGNATURE PROFILE ERROR:", err);
  }
}

async function handleSaveSignatureProfile() {
  try {
    setSavingSignatureProfile(true);
    setError("");

    await mailboxAxios.patch("/mailbox/signature-profile/me", signatureProfile);

    await loadSignatureProfile();
    await loadSignatures();
    setSignatureProfileOpen(false);
  } catch (err) {
    console.error("SAVE SIGNATURE PROFILE ERROR:", err);
    setError(err?.response?.data?.error || "Failed to save signature profile");
  } finally {
    setSavingSignatureProfile(false);
  }
}

  function openThread(threadId) {
    setSelectedThreadId(threadId);
    setMobileView("thread");
    setFoldersOpen(false);
  }

  function parseEmails(value) {
    if (Array.isArray(value)) return value;
    try {
      return JSON.parse(value || "[]");
    } catch {
      return [];
    }
  }

  return (
    <div className="h-[calc(100vh-80px)] min-h-0 flex flex-col lg:flex-row bg-white dark:bg-neutral-950 text-neutral-900 dark:text-neutral-100 overflow-hidden">
      {/* Left sidebar - desktop only */}
      <aside className="hidden lg:block w-60 shrink-0 border-r border-neutral-200 dark:border-neutral-800 p-4 overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Mailbox</h2>

        <div className="space-y-2 text-sm">
  {folders.map((folder) => {
    const active = activeFolder?.uniqueKey === folder.uniqueKey;

    return (
      <button
        key={folder.uniqueKey}
        onClick={() => setActiveFolder(folder)}
        className={`w-full text-left px-3 py-2 rounded-xl ${
          active
            ? "bg-neutral-100 dark:bg-neutral-900"
            : "hover:bg-neutral-100 dark:hover:bg-neutral-900"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="truncate">{folder.name || folder.folder_name}</span>
          <span className="text-xs text-neutral-500 shrink-0">
            {Number(folder.unread_messages || 0) > 0
              ? folder.unread_messages
              : folder.total_messages || 0}
          </span>
        </div>
      </button>
    );
  })}
</div>
      </aside>

      {/* Thread list */}
      <section
  className={`
    ${mobileView === "thread" ? "hidden" : "flex"}
    lg:flex w-full lg:w-[360px] xl:w-[400px]
    border-r border-neutral-200 dark:border-neutral-800
    flex-col min-w-0 min-h-0 overflow-hidden
  `}
>
        <div className="shrink-0 p-3 sm:p-4 border-b border-neutral-200 dark:border-neutral-800">
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-2">
  <h3 className="font-semibold">Threads</h3>

  <div className="flex items-center gap-2">
  <button
    onClick={() => setFoldersOpen(true)}
    className="lg:hidden px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm shrink-0"
  >
    Folders
  </button>

  <button
  onClick={() => {
  resetComposeForm();

  const defaultSignature =
    signatures.find((s) => Number(s.is_default) === 1) || signatures[0] || null;

  const editableDefaultSignature =
    signatures.find(
      (s) => Number(s.is_default) === 1 && !Number(s.is_auto_generated)
    ) ||
    signatures.find((s) => !Number(s.is_auto_generated)) ||
    null;

  setSignatureForm(
    editableDefaultSignature
      ? {
          id: editableDefaultSignature.id,
          name: editableDefaultSignature.name || "",
          html_signature: editableDefaultSignature.html_signature || "",
          is_default: Number(editableDefaultSignature.is_default) === 1,
          is_auto_generated: Number(editableDefaultSignature.is_auto_generated) === 1,
          source_type: editableDefaultSignature.source_type || "manual",
        }
      : {
          id: null,
          name: "",
          html_signature: "",
          is_default: false,
          is_auto_generated: false,
          source_type: "manual",
        }
  );

  setSelectedSignatureId(defaultSignature ? String(defaultSignature.id) : "");
  setComposeOpen(true);
  setActiveRecipientBox(null);
}}
  className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm shrink-0"
>
  Compose
</button>

  <button
    onClick={refreshMailbox}
    disabled={!activeFolder || activeFolder.isVirtual}
    className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
  >
    Refresh
  </button>
</div>
</div>

            <select
              value={accountId || ""}
              onChange={(e) => setAccountId(Number(e.target.value))}
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm bg-white dark:bg-neutral-900 min-w-0"
            >
              {accounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.display_name || acc.email}
                </option>
              ))}
            </select>

<div className="lg:hidden text-xs text-neutral-500">
  Folder: <span className="font-medium text-neutral-700 dark:text-neutral-300">
    {activeFolder?.name || activeFolder?.folder_name || "—"}
  </span>
</div>

          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y">
          {loadingThreads && (
            <div className="p-4 text-sm text-neutral-500">Loading...</div>
          )}

          {!loadingThreads && threads.length === 0 && (
            <div className="p-4 text-sm text-neutral-500">No threads</div>
          )}

          {threads.map((thread) => {
            const active = selectedThreadId === thread.id;

            return (
              <button
                key={thread.id}
                onClick={() => openThread(thread.id)}
                className={`w-full text-left p-4 border-b border-neutral-200 dark:border-neutral-800 ${
                  active
                    ? "bg-neutral-100 dark:bg-neutral-900"
                    : "hover:bg-neutral-50 dark:hover:bg-neutral-900/60"
                }`}
              >
                <div className="text-sm font-semibold truncate">
                  {thread.last_from_name || thread.last_from_email || "Unknown"}
                </div>
                <div className="text-sm truncate mt-1">
                  {thread.subject || "(No subject)"}
                </div>
                <div className="text-xs text-neutral-500 truncate mt-1">
                  {thread.last_snippet || ""}
                </div>
                <div className="text-xs text-neutral-400 mt-2">
                  {thread.message_count} messages
                </div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Thread detail */}
      <main
  className={`
    ${mobileView === "list" ? "hidden" : "flex"}
    lg:flex flex-1 flex-col min-w-0 min-h-0 overflow-hidden
  `}
>
        {!selectedThreadId && (
          <div className="flex-1 flex items-center justify-center text-neutral-500 p-6 text-center">
            Select a thread
          </div>
        )}

        {error && (
          <div className="m-4 p-3 rounded-xl bg-red-50 text-red-700 border border-red-200">
            {error}
          </div>
        )}

        {selectedThreadId && (
          <>
            <div className="shrink-0 p-4 sm:p-5 border-b border-neutral-200 dark:border-neutral-800">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2 lg:hidden">
                    <button
                      onClick={() => setMobileView("list")}
                      className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
                    >
                      Back
                    </button>
                  </div>

                  <h1 className="text-lg sm:text-xl font-semibold break-words">
                    {selectedThread?.thread?.subject || "(No subject)"}
                  </h1>
                  <div className="text-sm text-neutral-500 mt-1 break-all">
                    Thread #{selectedThread?.thread?.id}
                  </div>
                </div>

                {loadingThread && (
                  <div className="text-xs text-neutral-400 shrink-0">
                    Loading...
                  </div>
                )}
              </div>
            </div>

            {selectedThread && !loadingThread && (
              <>
                <div className="shrink-0 p-3 sm:p-4 border-b border-neutral-200 dark:border-neutral-800">
  <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 p-3 sm:p-4">
    <div className="text-sm font-semibold mb-3">Linked CRM</div>

    <div className="grid grid-cols-2 gap-3 text-xs sm:text-sm">
      <div className="space-y-1 min-w-0">
        <div className="text-[11px] sm:text-xs uppercase tracking-wide text-neutral-500">
          Contact
        </div>
        {selectedThread.crm?.contact ? (
          <div className="min-w-0">
            <div className="font-medium break-words">
              {selectedThread.crm.contact.FullName}
            </div>
            <div className="text-neutral-500 break-all">
              {selectedThread.crm.contact.Email || "—"}
            </div>
            <div className="text-neutral-500 break-all">
              {selectedThread.crm.contact.Phone || "—"}
            </div>
          </div>
        ) : (
          <div className="text-neutral-500">Not linked</div>
        )}
      </div>

      <div className="space-y-1 min-w-0">
        <div className="text-[11px] sm:text-xs uppercase tracking-wide text-neutral-500">
          Company
        </div>
        {selectedThread.crm?.party ? (
          <div className="min-w-0">
            <div className="font-medium break-words">
              {selectedThread.crm.party.DisplayName}
            </div>
            <div className="text-neutral-500 break-words">
              {[
                selectedThread.crm.party.City,
                selectedThread.crm.party.Country,
              ]
                .filter(Boolean)
                .join(", ") || "—"}
            </div>
          </div>
        ) : (
          <div className="text-neutral-500">Not linked</div>
        )}
      </div>

      <div className="space-y-1 min-w-0">
        <div className="text-[11px] sm:text-xs uppercase tracking-wide text-neutral-500">
          Lead
        </div>
        {selectedThread.crm?.lead ? (
          <div className="min-w-0">
            <div className="font-medium break-words">
              {selectedThread.crm.lead.Name}
            </div>
            <div className="text-neutral-500 break-all">
              {selectedThread.crm.lead.Email || "—"}
            </div>
            <div className="text-neutral-500 break-words">
              Status: {selectedThread.crm.lead.Status || "—"}
            </div>
          </div>
        ) : (
          <div className="text-neutral-500">Not linked</div>
        )}
      </div>

      <div className="space-y-1 min-w-0">
        <div className="text-[11px] sm:text-xs uppercase tracking-wide text-neutral-500">
          Opportunity
        </div>
        {selectedThread.crm?.opportunity ? (
          <div className="min-w-0">
            <div className="font-medium break-words">
              {selectedThread.crm.opportunity.Name}
            </div>
            <div className="text-neutral-500 break-words">
              {selectedThread.crm.opportunity.Account || "—"}
            </div>
            <div className="text-neutral-500 break-words">
              {selectedThread.crm.opportunity.Amount != null
                ? `${selectedThread.crm.opportunity.Amount} ${selectedThread.crm.opportunity.Currency || ""}`
                : "—"}
            </div>
          </div>
        ) : (
          <div className="text-neutral-500">Not linked</div>
        )}
      </div>
    </div>

    <div className="mt-3 flex flex-wrap gap-2">
      {selectedThread.crm?.contact?.Id && (
        <button
          className="px-2.5 py-1.5 rounded-xl border border-neutral-300 dark:border-neutral-700 text-xs sm:text-sm"
          onClick={() => (window.location.href = "/crm")}
        >
          Open CRM
        </button>
      )}

      <button
        className="px-2.5 py-1.5 rounded-xl border border-neutral-300 dark:border-neutral-700 text-xs sm:text-sm"
        onClick={() => alert("Next step: manual CRM linking")}
      >
        Link manually
      </button>
    </div>
  </div>
</div>

                <div
  ref={threadScrollRef}
  className="flex-1 min-h-0 overflow-y-auto overscroll-contain touch-pan-y p-4 sm:p-5 space-y-4"
>
                  {selectedThread.messages?.map((message) => (
                    <div
                      key={message.id}
                      className="border border-neutral-200 dark:border-neutral-800 rounded-2xl p-4 overflow-hidden"
                    >

<div className="mt-3 flex flex-wrap gap-2">
  <button
  className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
  onClick={async () => {
    try {
      await mailboxAxios.patch(`/mailbox/messages/${message.id}/star`, {
        is_starred: !message.is_starred,
      });
      await loadThread(selectedThreadId, { preserveScroll: true });
      await loadThreads({ preserveSelection: true });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update star");
    }
  }}
>
  {message.is_starred ? "Unstar" : "Star"}
</button>

  <button
  className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
  onClick={async () => {
    try {
      await mailboxAxios.patch(`/mailbox/messages/${message.id}/read`, {
        is_read: !message.is_read,
      });
      await loadThread(selectedThreadId, { preserveScroll: true });
      await loadThreads({ preserveSelection: true });
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update read state");
    }
  }}
>
  {message.is_read ? "Mark unread" : "Mark read"}
</button>

  <button
  className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
  onClick={async () => {
    try {
      await mailboxAxios.patch(`/mailbox/messages/${message.id}/trash`, {
        is_deleted: !message.is_deleted,
      });
      await loadThreads({ preserveSelection: true });
      if (selectedThreadId) {
        await loadThread(selectedThreadId, { preserveScroll: true });
      }
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to update trash state");
    }
  }}
>
  {message.is_deleted ? "Restore" : "Move to trash"}
</button>
</div>

                      <div className="flex flex-col py-4 sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div className="min-w-0">
                          <div className="font-medium break-words">
                            {message.from_name || message.from_email || "Unknown"}
                          </div>
                          <div className="text-sm text-neutral-500 mt-1 break-all">
                            {message.from_email}
                          </div>
                          <div className="text-sm text-neutral-500 mt-1 break-words">
                            To: {parseEmails(message.to_emails).join(", ")}
                          </div>
                        </div>

                        <div className="text-xs text-neutral-400 whitespace-nowrap shrink-0">
                          {message.received_at || message.sent_at || ""}
                        </div>
                      </div>

                      <div className="mt-4 text-sm min-w-0 overflow-hidden">
                        {message.html_body ? (
                          <div
                            className="prose prose-sm max-w-none dark:prose-invert break-words [&_*]:max-w-full [&_img]:h-auto [&_img]:max-w-full [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_pre]:whitespace-pre-wrap [&_pre]:break-words"
                            dangerouslySetInnerHTML={{ __html: message.html_body }}
                          />
                        ) : (
                          <pre className="whitespace-pre-wrap break-words font-sans text-sm">
                            {message.text_body || ""}
                          </pre>
                        )}
                      </div>

                      {!!message.attachments?.length && (
                        <div className="mt-4">
                          <div className="text-sm font-medium mb-2">
                            Attachments
                          </div>
                          <div className="space-y-2">
                            {message.attachments.map((att) => (
                              <div
                                key={att.id}
                                className="text-sm px-3 py-2 rounded-xl bg-neutral-100 dark:bg-neutral-900 break-all"
                              >
                                {att.filename}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </main>

{composeOpen && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="font-semibold">
          {composeForm.draftId ? "Edit Draft" : "Compose Email"}
        </div>

        <button
          onClick={() => {
            setComposeOpen(false);
            resetComposeForm();
          }}
          className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
        >
          Close
        </button>
      </div>

      <div className="p-4 space-y-3 max-h-[80vh] overflow-y-auto">
        <div>
          <div className="text-sm mb-1">From</div>
          <div className="px-3 py-2 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-900 text-sm">
            {accounts.find((a) => a.id === accountId)?.display_name ||
              accounts.find((a) => a.id === accountId)?.email ||
              "Selected account"}
          </div>
        </div>

        <div>
          {renderRecipientField("to", "To", "Type name or email...")}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
  {renderRecipientField("cc", "Cc", "Type name or email...")}
  {renderRecipientField("bcc", "Bcc", "Type name or email...")}
</div>

        <div>
  <div className="text-sm mb-1">Subject</div>
  <input
    value={composeForm.subject}
    onChange={(e) =>
      setComposeForm((prev) => ({ ...prev, subject: e.target.value }))
    }
    placeholder="Subject"
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>



<div>
  <div className="text-sm mb-1">Message</div>
  <textarea
    value={composeForm.text}
    onChange={(e) =>
      setComposeForm((prev) => ({
        ...prev,
        text: e.target.value,
        html: e.target.value.replace(/\n/g, "<br />"),
      }))
    }
    placeholder="Write your message..."
    rows={12}
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm resize-y"
  />
</div>

<div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-3 items-end">
  <div>
    <div className="text-sm mb-1">Signature</div>
    <select
      value={selectedSignatureId}
      onChange={(e) => setSelectedSignatureId(e.target.value)}
      className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
    >
      <option value="">No signature</option>
      {signatures.map((sig) => (
        <option key={sig.id} value={sig.id}>
          {sig.name}
          {Number(sig.is_default) === 1 ? " (default)" : ""}
          {Number(sig.is_auto_generated) === 1 ? " [template]" : ""}
        </option>
      ))}
    </select>
  </div>

   <button
    type="button"
    onClick={() => setSignaturePreviewOpen(true)}
    disabled={!selectedSignatureId}
    className="px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm disabled:opacity-50"
  >
    Preview
  </button>

  {/* <button
  type="button"
  onClick={() => {
    const editableSignature =
      signatures.find((s) => !Number(s.is_auto_generated) && Number(s.is_default) === 1) ||
      signatures.find((s) => !Number(s.is_auto_generated)) ||
      null;

    setSignatureForm(
      editableSignature
        ? {
            id: editableSignature.id,
            name: editableSignature.name || "",
            html_signature: editableSignature.html_signature || "",
            is_default: Number(editableSignature.is_default) === 1,
            is_auto_generated: Number(editableSignature.is_auto_generated) === 1,
            source_type: editableSignature.source_type || "manual",
          }
        : {
            id: null,
            name: "",
            html_signature: "",
            is_default: false,
            is_auto_generated: false,
            source_type: "manual",
          }
    );

    setSignatureEditorOpen(true);
  }}
  className="px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
>
  Manage
</button>

  <button
    type="button"
    onClick={() => setSignatureProfileOpen(true)}
    className="px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
  >
    Profile
  </button>  */}
</div>

       
      </div>

      <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-end gap-2">
        <button
          onClick={handleSaveDraft}
          disabled={savingDraft || sending}
          className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm disabled:opacity-50"
        >
          {savingDraft ? "Saving..." : composeForm.draftId ? "Update Draft" : "Save Draft"}
        </button>

        <button
          onClick={handleSendEmail}
          disabled={sending || savingDraft}
          className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm disabled:opacity-50"
        >
          {sending ? "Sending..." : "Send"}
        </button>
      </div>
    </div>
  </div>
)}

{foldersOpen && (
  <div className="lg:hidden fixed inset-0 z-40">
    <div
      className="absolute inset-0 bg-black/40"
      onClick={() => setFoldersOpen(false)}
    />

    <div className="absolute left-0 top-0 h-full w-[280px] max-w-[85vw] bg-white dark:bg-neutral-950 border-r border-neutral-200 dark:border-neutral-800 shadow-xl flex flex-col">
      <div className="shrink-0 flex items-center justify-between px-4 py-4 border-b border-neutral-200 dark:border-neutral-800">
        <h2 className="text-lg font-semibold">Mailbox</h2>

        <button
          onClick={() => setFoldersOpen(false)}
          className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
        >
          Close
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        <div className="space-y-2 text-sm">
          {folders.map((folder) => {
            const active = activeFolder?.uniqueKey === folder.uniqueKey;

            return (
              <button
                key={folder.uniqueKey}
                onClick={() => {
                  setActiveFolder(folder);
                  setFoldersOpen(false);
                  setMobileView("list");
                }}
                className={`w-full text-left px-3 py-2 rounded-xl ${
                  active
                    ? "bg-neutral-100 dark:bg-neutral-900"
                    : "hover:bg-neutral-100 dark:hover:bg-neutral-900"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate">
                    {folder.name || folder.folder_name}
                  </span>
                  <span className="text-xs text-neutral-500 shrink-0">
                    {Number(folder.unread_messages || 0) > 0
                      ? folder.unread_messages
                      : folder.total_messages || 0}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  </div>
)}

{signatureEditorOpen && (
  <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="font-semibold">
          {signatureForm.id ? "Edit Signature" : "New Signature"}
        </div>

        <button
          onClick={() => setSignatureEditorOpen(false)}
          className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
        >
          Close
        </button>
      </div>

      <div className="p-4 space-y-3 max-h-[80vh] overflow-y-auto">
        <div>
          <div className="text-sm mb-1">Name</div>
          <input
            value={signatureForm.name}
            disabled={isLockedSignature}
            onChange={(e) =>
              setSignatureForm((prev) => ({ ...prev, name: e.target.value }))
            }
            placeholder="Signature name"
            className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
          />
        </div>

        <div className="flex items-center gap-2">
          <input
  id="signature-default"
  type="checkbox"
  checked={!!signatureForm.is_default}
  disabled={isLockedSignature}
  onChange={(e) =>
    setSignatureForm((prev) => ({
      ...prev,
      is_default: e.target.checked,
    }))
  }
/>
          <label htmlFor="signature-default" className="text-sm">
            Set as default
          </label>
        </div>

        <div>
          <div className="text-sm mb-1">HTML Signature</div>
          <textarea
            value={signatureForm.html_signature}
            disabled={isLockedSignature}
            onChange={(e) =>
              setSignatureForm((prev) => ({
                ...prev,
                html_signature: e.target.value,
              }))
            }
            rows={16}
            className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm font-mono"
          />

{isLockedSignature && (
  <div className="rounded-xl border border-blue-200 bg-blue-50 text-blue-700 px-3 py-2 text-sm dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-900">
    This signature is generated from an admin template and cannot be edited manually.
    Update your signature profile instead.
  </div>
)}

        </div>

        <div>
          <div className="text-sm mb-2">Preview</div>
          <div
  className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-4 bg-white dark:bg-neutral-900 overflow-auto prose prose-sm max-w-none dark:prose-invert [&_*]:max-w-full [&_img]:h-auto [&_img]:max-w-full [&_table]:max-w-full [&_table]:block [&_table]:overflow-x-auto"
  dangerouslySetInnerHTML={{
    __html: signatureForm.html_signature || "<em>No signature preview</em>",
  }}
/>
        </div>

        {!!signatures.length && (
          <div>
            <div className="text-sm font-medium mb-2">Existing signatures</div>
            <div className="space-y-2">
              {signatures.map((sig) => (
                <div
                  key={sig.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 rounded-xl border border-neutral-200 dark:border-neutral-800 p-3"
                >
                  <div className="min-w-0">
                    <div className="font-medium flex flex-wrap items-center gap-2">
  <span>{sig.name}</span>
  {Number(sig.is_default) === 1 && (
    <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 dark:bg-neutral-800">
      Default
    </span>
  )}
  {Number(sig.is_auto_generated) === 1 && (
    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
      Template
    </span>
  )}
</div>
<div className="text-xs text-neutral-500 mt-1">
  {Number(sig.is_auto_generated) === 1
    ? `Generated from template${sig.template_name ? `: ${sig.template_name}` : ""}`
    : "Manual signature"}
</div>
                  </div>

                  <div className="flex flex-wrap gap-2">
  {!Number(sig.is_auto_generated) && (
    <button
      type="button"
      onClick={() =>
        setSignatureForm({
  id: sig.id,
  name: sig.name || "",
  html_signature: sig.html_signature || "",
  is_default: Number(sig.is_default) === 1,
  is_auto_generated: Number(sig.is_auto_generated) === 1,
  source_type: sig.source_type || "manual",
})
      }
      className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
    >
      Edit
    </button>
  )}

  <button
    type="button"
    onClick={async () => {
      try {
        await mailboxAxios.post(`/mailbox/signatures/${sig.id}/default`);
        await loadSignatures();
      } catch (err) {
        setError(err?.response?.data?.error || "Failed to set default signature");
      }
    }}
    className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
  >
    Set default
  </button>

  {!Number(sig.is_auto_generated) && (
    <button
      type="button"
      onClick={async () => {
        try {
          await mailboxAxios.delete(`/mailbox/signatures/${sig.id}`);
          await loadSignatures();
        } catch (err) {
          setError(err?.response?.data?.error || "Failed to delete signature");
        }
      }}
      className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
    >
      Delete
    </button>
  )}
</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() =>
            setSignatureForm({
  id: null,
  name: "",
  html_signature: "",
  is_default: false,
  is_auto_generated: false,
  source_type: "manual",
})
          }
          className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
        >
          New
        </button>

        <button
  type="button"
  disabled={isLockedSignature}
  onClick={async () => {
    try {
      if (isLockedSignature) {
        setError("Template-generated signature cannot be edited manually");
        return;
      }

      if (!signatureForm.name.trim()) {
        setError("Signature name is required");
        return;
      }

      if (signatureForm.id) {
        await mailboxAxios.patch(`/mailbox/signatures/${signatureForm.id}`, {
          name: signatureForm.name,
          html_signature: signatureForm.html_signature,
          is_default: signatureForm.is_default,
        });
      } else {
        await mailboxAxios.post(`/mailbox/signatures`, {
          name: signatureForm.name,
          html_signature: signatureForm.html_signature,
          is_default: signatureForm.is_default,
        });
      }

      await loadSignatures();
      setSignatureEditorOpen(false);
    } catch (err) {
      setError(err?.response?.data?.error || "Failed to save signature");
    }
  }}
  className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm disabled:opacity-50"
>
  Save signature
</button>
      </div>
    </div>
  </div>
)}

{signatureProfileOpen && (
  <div className="fixed inset-0 z-[65] flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-3xl rounded-2xl bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="font-semibold">My Signature Profile</div>

        <button
          onClick={() => setSignatureProfileOpen(false)}
          className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
        >
          Close
        </button>
      </div>

      <div className="p-4 space-y-3 max-h-[80vh] overflow-y-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <div className="text-sm mb-1">Full name</div>
            <input
              value={signatureProfile.full_name || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, full_name: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">Display name</div>
            <input
              value={signatureProfile.display_name || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, display_name: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">Email</div>
            <input
              value={signatureProfile.email || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, email: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">Job title</div>
            <input
              value={signatureProfile.job_title || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, job_title: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">Department</div>
            <input
              value={signatureProfile.department || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, department: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">Phone</div>
            <input
              value={signatureProfile.phone || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, phone: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">Mobile</div>
            <input
              value={signatureProfile.mobile || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, mobile: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">Website</div>
            <input
              value={signatureProfile.website || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, website: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">LinkedIn URL</div>
            <input
              value={signatureProfile.linkedin_url || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, linkedin_url: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
  <div className="text-sm mb-1">Pinterest URL</div>
  <input
    value={signatureProfile.pinterest_url || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, pinterest_url: e.target.value }))
    }
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

<div>
  <div className="text-sm mb-1">X / Twitter URL</div>
  <input
    value={signatureProfile.twitter_url || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, twitter_url: e.target.value }))
    }
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

<div>
  <div className="text-sm mb-1">WhatsApp URL</div>
  <input
    value={signatureProfile.whatsapp_url || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, whatsapp_url: e.target.value }))
    }
    placeholder="https://wa.me/381628541543"
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

<div>
  <div className="text-sm mb-1">Telegram URL</div>
  <input
    value={signatureProfile.telegram_url || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, telegram_url: e.target.value }))
    }
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

<div>
  <div className="text-sm mb-1">Instagram URL</div>
  <input
    value={signatureProfile.instagram_url || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, instagram_url: e.target.value }))
    }
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

<div>
  <div className="text-sm mb-1">Facebook URL</div>
  <input
    value={signatureProfile.facebook_url || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, facebook_url: e.target.value }))
    }
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

<div>
  <div className="text-sm mb-1">YouTube URL</div>
  <input
    value={signatureProfile.youtube_url || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, youtube_url: e.target.value }))
    }
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

<div>
  <div className="text-sm mb-1">vCard URL</div>
  <input
    value={signatureProfile.vcard_url || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, vcard_url: e.target.value }))
    }
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

<div className="md:col-span-2">
  <div className="text-sm mb-1">Tagline</div>
  <input
    value={signatureProfile.tagline || ""}
    onChange={(e) =>
      setSignatureProfile((prev) => ({ ...prev, tagline: e.target.value }))
    }
    className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
  />
</div>

          <div>
            <div className="text-sm mb-1">Logo URL</div>
            <input
              value={signatureProfile.logo_url || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, logo_url: e.target.value }))
              }
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>

          <div>
            <div className="text-sm mb-1">Accent color</div>
            <input
              value={signatureProfile.accent_color || ""}
              onChange={(e) =>
                setSignatureProfile((prev) => ({ ...prev, accent_color: e.target.value }))
              }
              placeholder="#111111"
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 bg-white dark:bg-neutral-900 text-sm"
            />
          </div>
        </div>
      </div>

      <div className="px-4 py-3 border-t border-neutral-200 dark:border-neutral-800 flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={handleSaveSignatureProfile}
          disabled={savingSignatureProfile}
          className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm disabled:opacity-50"
        >
          {savingSignatureProfile ? "Saving..." : "Save profile"}
        </button>
      </div>
    </div>
  </div>
)}

{signaturePreviewOpen && (
  <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-2xl rounded-2xl bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-800 shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-800">
        <div className="font-semibold">Signature Preview</div>

        <button
          onClick={() => setSignaturePreviewOpen(false)}
          className="px-3 py-1.5 rounded-lg border border-neutral-300 dark:border-neutral-700 text-sm"
        >
          Close
        </button>
      </div>

      <div className="p-4 max-h-[75vh] overflow-auto">
        <div
          className="rounded-xl border border-neutral-200 dark:border-neutral-800 p-4 bg-white dark:bg-neutral-900 overflow-auto"
          dangerouslySetInnerHTML={{
            __html:
              signatures.find((s) => String(s.id) === String(selectedSignatureId))
                ?.html_signature || "<em>No signature preview</em>",
          }}
        />
      </div>
    </div>
  </div>
)}

    </div>
  );
}